import { useEffect, useRef, useState, lazy, Suspense } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import SyncLoader from "react-spinners/SyncLoader"
import { Box, Typography, useMediaQuery, Stack, useTheme, Fade } from "@mui/material"
import { setViewByLabel, setRefreshKey } from "state"
import Navbar from "views/navbar"
import LabelsDrawer from "components/LabelsDrawer"
import { BsBookmarkStar, BsEmojiLaughing, BsEmojiSmile, BsCalendar4Week, BsSuitSpade } from "react-icons/bs";
import { BiHomeAlt2, BiPulse } from "react-icons/bi";
import { PiCircleBold, PiDiamondBold, PiStarBold, PiBathtub, PiLinkBold, PiClock } from "react-icons/pi";
import { MdLabelOutline } from "react-icons/md";
import { TbTemperature, TbToolsKitchen2, TbRulerMeasure, TbStars } from "react-icons/tb";
import { IoHandRightOutline, IoLanguage, IoBeerOutline, IoBodyOutline, IoBedOutline, IoFastFoodOutline, IoPeopleOutline } from "react-icons/io5";
import { LiaFeatherAltSolid } from "react-icons/lia";
import { FaRegHeart } from "react-icons/fa";
import { FaRegUser } from "react-icons/fa6";
import { FiThumbsUp, FiThumbsDown, FiMapPin, FiBriefcase, FiHash } from "react-icons/fi";
import { RiHeartsLine, RiChat3Line, RiWechatLine, RiGamepadLine } from "react-icons/ri";
import { HiOutlineBookOpen } from "react-icons/hi2";
import ViewVocabsWidget from "views/widgets/ViewVocabsWidget"
import apiUrl from "config/api"
const MobileFooterNavigation = lazy(() => import("../widgets/MobileFooterNavigation"))
const DesktopFooter = lazy(() => import("../widgets/DesktopFooter"))

const SearchPage = () => {
  const isNonMobileScreens = useMediaQuery("(min-width:1000px) and (max-height:2160px)")
  const isHDScreens = useMediaQuery("(min-width:1280px) and (max-height:900px)")
  const { palette } = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const viewByLabel = useSelector((state) => state.viewByLabel)
  const viewBySearchTerm = useSelector((state) => state.viewBySearchTerm)
  const refreshKey = useSelector((state) => state.refreshKey)
  const mode = useSelector((state) => state.mode)

  const searchRef = useRef(null)
  const [fadeIn, setFadeIn] = useState(false)

  const isLandscape = window.matchMedia("(orientation: landscape)").matches;

  const theme = useTheme()

  const queryClient = useQueryClient()

  const token = useSelector((state) => state.token)
  const { _id } = useSelector((state) => state.user)

  const getVocabLabels = () => {
    return fetch(`${apiUrl}/labels/${_id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
  }

  const { data: labelsData } = useQuery(["vocabLabelsData"], getVocabLabels, {
    keepPreviousData: true,
    staleTime: 1000
  })

  const viewLabledVocabs = (text) => {
    navigate(`/label/${_id}`)
    dispatch(setViewByLabel({ viewByLabel: text }))
    dispatch(setRefreshKey({ refreshKey: refreshKey + 1 }))
    queryClient.invalidateQueries({ queryKey: ["labeledVocabsData"] })
  }

  useEffect(() => {
    if (viewByLabel !== "") {
      dispatch(setViewByLabel(""))
    }
  }, [viewByLabel, dispatch])

  useEffect(() => {
    setFadeIn(true)
  }, [])

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus()
    }
  }, [searchRef])

  const navigateHome = () => {
    navigate(`/`)
    dispatch(setRefreshKey({ refreshKey: 0 }))
  }
  const showAllVocabs = () => {
    navigate(`/all/${_id}`)
    dispatch(setRefreshKey({ refreshKey: refreshKey + 1 }))
  }
  const showIntermediateVocabs = () => {
    navigate(`/int/${_id}`)
    dispatch(setRefreshKey({ refreshKey: refreshKey + 1 }))
  }
  const showAdvancedVocabs = () => {
    navigate(`/adv/${_id}`)
    dispatch(setRefreshKey({ refreshKey: refreshKey + 1 }))
  }

  const renderIcon = (text) => {
    switch (text) {
      case 'action':
        return <IoHandRightOutline size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'body':
        return <IoBodyOutline size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'bathroom':
        return <PiBathtub size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'bedroom':
        return <IoBedOutline size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'colloquial':
        return <RiChat3Line size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'conjunction':
        return <PiLinkBold size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'conversation':
        return <RiWechatLine size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'degree':
        return <TbTemperature size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'estimation':
        return <TbRulerMeasure size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'expression':
        return <BsEmojiSmile size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'family':
        return <IoPeopleOutline size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'food':
        return <IoFastFoodOutline size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'frequency':
        return <BsCalendar4Week size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'gaming':
        return <RiGamepadLine size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'grammar':
        return <IoLanguage size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'house':
        return <BiHomeAlt2 size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'humor':
        return <BsEmojiLaughing size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'idiom':
        return <LiaFeatherAltSolid size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'kitchen':
        return <TbToolsKitchen2 size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'mood':
        return <FaRegHeart size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'negative':
        return <FiThumbsDown size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'personality':
        return <FaRegUser size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'places':
        return <FiMapPin size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'poker':
        return <BsSuitSpade size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'positive':
        return <FiThumbsUp size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'professional':
        return <FiBriefcase size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'rating':
        return <TbStars size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'romance':
        return <RiHeartsLine size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'slang':
        return <FiHash size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'social':
        return <IoBeerOutline size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'status':
        return <BiPulse size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'time':
        return <PiClock size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      case 'wisdom':
        return <HiOutlineBookOpen size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
      default:
        return <MdLabelOutline size={isLandscape ? 36 : 32} style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }} color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main} />;
    }
  };

  return (
    <Box>
      <Navbar searchRef={searchRef} />

      <Fade in={fadeIn} timeout={400}>
      <Box>

      {/* ----- Page Body ----- */}

      <LabelsDrawer />

      <Box
        width="100%"
        padding={isLandscape ? "1rem 4%" : "2rem 1%"}
        display={isNonMobileScreens ? "flex" : "block"}
        gap="0.5rem"
        justifyContent="center"
      >

        {/* ----- Mid Page Column ----- */}

        <Box
          flexBasis={isNonMobileScreens ? "64%" : undefined}
          mt={isNonMobileScreens ? undefined : "2rem"}
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          {
            !viewBySearchTerm ?
              (
                <>
                  <Typography
                    variant={isLandscape ? "h2" : "h3"}
                    textAlign={"center"}
                    color={palette.neutral.darker}
                  >
                    Search by Text or Vocab Label
                  </Typography>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    flexWrap={"wrap"}
                    sx={{
                      m: 2,
                      p: "1rem",
                      borderRadius: "1rem",
                    }}
                  >
                    <Stack
                      key={"pinned"}
                      onClick={navigateHome}
                      direction={"column"}
                      alignItems={"center"}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "2rem",
                        padding: "1rem",
                        border: `solid 1px ${palette.neutral.light}`,
                        m: 1,
                        '&:hover': {
                          backgroundColor: mode === "light" ? "rgba(155, 155, 171, 0.1)" : theme.palette.primary.light,
                          border: mode === "light" ? `solid 1px rgba(155, 155, 171, 0.1)` : `solid 1px ${palette.primary.light}`,
                        },
                      }}
                    >
                      <BsBookmarkStar
                        size={isLandscape ? 36 : 32}
                        style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }}
                        color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main}
                      />
                      <Typography sx={{ fontSize: isLandscape ? "0.8rem" : "0.65rem", fontWeight: mode === "light" ? 500 : 400 }}>
                        Studying
                      </Typography>
                    </Stack>
                    <Stack
                      key={"pinned"}
                      onClick={showAllVocabs}
                      direction={"column"}
                      alignItems={"center"}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "2rem",
                        padding: "1rem",
                        border: `solid 1px ${palette.neutral.light}`,
                        m: 1,
                        '&:hover': {
                          backgroundColor: mode === "light" ? "rgba(155, 155, 171, 0.1)" : theme.palette.primary.light,
                          border: mode === "light" ? `solid 1px rgba(155, 155, 171, 0.1)` : `solid 1px ${palette.primary.light}`,
                        },
                      }}
                    >
                      <PiCircleBold
                        size={isLandscape ? 36 : 32}
                        style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }}
                        color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main}
                      />
                      <Typography sx={{ fontSize: isLandscape ? "0.8rem" : "0.65rem", fontWeight: mode === "light" ? 500 : 400 }}>
                        All
                      </Typography>
                    </Stack>
                    <Stack
                      key={"pinned"}
                      onClick={showIntermediateVocabs}
                      direction={"column"}
                      alignItems={"center"}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "2rem",
                        padding: "1rem",
                        border: `solid 1px ${palette.neutral.light}`,
                        m: 1,
                        '&:hover': {
                          backgroundColor: mode === "light" ? "rgba(155, 155, 171, 0.1)" : theme.palette.primary.light,
                          border: mode === "light" ? `solid 1px rgba(155, 155, 171, 0.1)` : `solid 1px ${palette.primary.light}`,
                        },
                      }}
                    >
                      <PiDiamondBold
                        size={isLandscape ? 36 : 32}
                        style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }}
                        color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main}
                      />
                      <Typography sx={{ fontSize: isLandscape ? "0.8rem" : "0.65rem", fontWeight: mode === "light" ? 500 : 400 }}>
                        Lv. 2
                      </Typography>
                    </Stack>
                    <Stack
                      key={"pinned"}
                      onClick={showAdvancedVocabs}
                      direction={"column"}
                      alignItems={"center"}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "2rem",
                        padding: "1rem",
                        border: `solid 1px ${palette.neutral.light}`,
                        m: 1,
                        '&:hover': {
                          backgroundColor: mode === "light" ? "rgba(155, 155, 171, 0.1)" : theme.palette.primary.light,
                          border: mode === "light" ? `solid 1px rgba(155, 155, 171, 0.1)` : `solid 1px ${palette.primary.light}`,
                        },
                      }}
                    >
                      <PiStarBold
                        size={isLandscape ? 36 : 32}
                        style={{ margin: isLandscape ? "0.5rem 2rem" : "0.25rem 1.25rem" }}
                        color={mode === "light" ? theme.palette.neutral.darker : theme.palette.primary.main}
                      />
                      <Typography sx={{ fontSize: isLandscape ? "0.8rem" : "0.65rem", fontWeight: mode === "light" ? 500 : 400 }}>
                        Lv. 3
                      </Typography>
                    </Stack>
                    {labelsData?.[0].label.sort().map((text, index) => (
                      <Stack
                        key={text}
                        onClick={() => viewLabledVocabs(text)}
                        direction={"column"}
                        alignItems={"center"}
                        sx={{
                          cursor: "pointer",
                          borderRadius: "2rem",
                          padding: "1rem",
                          border: `solid 1px ${palette.neutral.light}`,
                          m: 1,
                          '&:hover': {
                            backgroundColor: mode === "light" ? "rgba(155, 155, 171, 0.1)" : theme.palette.primary.light,
                            border: mode === "light" ? `solid 1px rgba(155, 155, 171, 0.1)` : `solid 1px ${palette.primary.light}`,
                          },
                        }}
                      >
                        {renderIcon(text)}
                        <Typography
                          sx={{
                            fontSize: isLandscape ? "0.8rem" : "0.65rem",
                            fontWeight: mode === "light" ? 500 : 400
                          }}
                        >
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </>
              ) :
              <ViewVocabsWidget key={refreshKey} />
          }
        </Box>
        {isNonMobileScreens &&
          <Suspense fallback={
            <SyncLoader
              color={palette.neutral.light}
              loading={true}
              size={20}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          }>
            <DesktopFooter isHome />
          </Suspense>
        }
        {!isNonMobileScreens &&
          <Suspense fallback={
            <SyncLoader
              color={palette.neutral.light}
              loading={true}
              size={20}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          }>
            <MobileFooterNavigation isHome />
          </Suspense>
        }
      </Box>

      </Box>
      </Fade>
    </Box>
  )
}

export default SearchPage